/**
 * Stage → chart series STYLE: a colour and a texture, as design-system utility
 * classes plus a small integer.
 *
 * WHAT JARED ASKED FOR AND WHAT IT COSTS. 2026-08-25, on the dashboard: *"we
 * should make sure there's no two, it doesn't repeat colors… That seems like a
 * pretty fundamental thing, I don't know why that would even happen."* and, on
 * the two widgets side by side, *"the reference in the chart should be the same
 * like the current [stage]"*. Two requirements: **no two visible series look
 * alike**, and **one stage looks the same everywhere**.
 *
 * The scale has EIGHT hues (`--chart-1` … `--chart-8`) plus `--chart-neutral`.
 * `JobSearchStage` has thirteen non-neutral names today and Thrive's vocabulary
 * is tenant-configurable — 24 stage definitions on 2026-05-06 — so "thirteen
 * distinct hues" is an equation with no solution. Something has to give, and
 * there were only ever two candidates:
 *
 *   (a) drop "the same everywhere" and assign colours per rendered set, so each
 *       chart gets eight distinct hues out of its own stages; or
 *   (b) keep "the same everywhere" and add a SECOND CHANNEL — a dash on a line,
 *       a hatch on a fill — so two stages sharing a hue are still two things.
 *
 * THIS MODULE IMPLEMENTS (b), and the reason is that (a) breaks the requirement
 * Jared actually named. The Search Dashboard draws the union of the donut's
 * stages and the funnel's rungs; the Search Progress tab draws every stage the
 * search has ever touched; the downloaded report draws the donut's alone. Three
 * different sets, so a set-scoped assignment hands `Reference` three different
 * hues across one search — which is the same defect he reported, moved one tab
 * to the right. A previous revision of this file shipped (a) for the donut and
 * the funnel while the lines kept (b), and the two disagreed on all five of the
 * pairs the fix was about.
 *
 * SO THE ASSIGNMENT IS GLOBAL AND TOTAL: {@link stageSeriesStyle} is a pure
 * function of the stage NAME. Not of the chart, not of the set, not of the
 * order — which is what makes "the same stage looks the same in the Dashboard,
 * in Search Progress and in the downloaded report" structurally true rather
 * than a thing three call sites have to keep agreeing about.
 *
 * THE STYLE IS A PAIR — (hue, tier) — and the pair, not the hue, is what never
 * repeats:
 *
 *   - Tier 0 is solid: a plain line, a flat fill. The EIGHT LADDER RUNGS take
 *     the eight hues at tier 0, one apiece. They are the stages that appear
 *     together on essentially every search, so they are the collision a reader
 *     meets daily, and they are the ones that must be readable with no texture
 *     at all.
 *   - Tiers 1-3 are dashed (a line) or hatched (a fill). The five AUXILIARY
 *     stages — roles, holds, references — sit at tier 1 on a hue they share
 *     with a rung. `Carve Out` is a dashed `Source`, not a second `Source`.
 *
 * Thirteen names, thirteen distinct pairs, and 32 pairs exist — so the scale is
 * not close to exhausted even before the neutral stages, which cost nothing
 * because they are a rule rather than an assignment (see below).
 *
 * NEUTRAL STAGES ARE A DELIBERATE COLLISION, NOT A FAILURE. `Rejected`,
 * `Unknown`, `Unresponsive`, `On Hold` and `Backburner` all render grey and
 * solid, so a chart of a closed-out search reads as mostly-grey at a glance
 * instead of a festive spread of eight hues. They are excluded from every
 * uniqueness claim this module makes, on purpose.
 *
 * UNMAPPED STAGES get a pair hashed into the slots the map does NOT claim, so a
 * tenant stage can never be mistaken for `Placed`. Two unmapped stages can
 * still collide with each other — nineteen free pairs against an open-ended
 * vocabulary — and that is the one residual, documented rather than papered
 * over.
 *
 * THAT RESIDUAL IS A DELIBERATE TRADE, not an oversight, and it is worth being
 * plain about which way it goes. A set-scoped assignment (what this replaced)
 * could guarantee that no two stages drawn TOGETHER share a style, but only by
 * reading the set — which made the same stage look different on the Dashboard
 * and on Search Progress. A pure function of the name cannot read the set, so
 * it cannot make that guarantee for names it does not know. Purity and
 * in-set uniqueness genuinely cannot both hold; this file chose purity, because
 * cross-view consistency is what the charts are read for and hash collisions
 * among unmapped names are rare. Verified against the live catalogue: the five
 * unmapped names in `.agent/SOP/05-thrive-api/domain-model.md` (`Short List`,
 * `Thrive IQ Recommendations`, and the three TalentPool horizons) come out
 * five-for-five distinct today.
 *
 * Utility CLASSES, not hex: the classes resolve to the `--chart-*` tokens,
 * which is what keeps the charts theme-aware and inline-style-free. The TIER is
 * a number rather than a class because its two renderings are different SVG
 * mechanisms — `stroke-dasharray` on a line, a `<pattern>` fill on a shape (see
 * `StageHatch.tsx`) — and a class name could express neither.
 */

/** Tailwind `fill-*` classes for the categorical scale. */
const SERIES_FILL = [
  "fill-chart-1",
  "fill-chart-2",
  "fill-chart-3",
  "fill-chart-4",
  "fill-chart-5",
  "fill-chart-6",
  "fill-chart-7",
  "fill-chart-8",
] as const;

/** The matching `bg-*` classes, for legend swatches and horizontal bars. */
const SERIES_BG = [
  "bg-chart-1",
  "bg-chart-2",
  "bg-chart-3",
  "bg-chart-4",
  "bg-chart-5",
  "bg-chart-6",
  "bg-chart-7",
  "bg-chart-8",
] as const;

/** The matching `stroke-*` classes, for line series. */
const SERIES_STROKE = [
  "stroke-chart-1",
  "stroke-chart-2",
  "stroke-chart-3",
  "stroke-chart-4",
  "stroke-chart-5",
  "stroke-chart-6",
  "stroke-chart-7",
  "stroke-chart-8",
] as const;

/**
 * THE SECOND CHANNEL, as SVG `stroke-dasharray` strings in viewBox units. Index
 * 0 is solid; a filled shape renders the same index as a hatch density.
 *
 * Adding `--chart-9` … `--chart-13` was the other way to reach thirteen and was
 * NOT taken. It is a design-system change shared with the extension, the
 * existing eight are documented as tuned against both themes (a new hue would
 * have to be tuned the same way), and five more hues still would not help a
 * reader who cannot tell violet from indigo. A dash survives both themes and
 * colour blindness unchanged — and the vocabulary is tenant-configurable, so
 * the hue count would have to chase it forever.
 */
const SERIES_DASH = ["", "5 3", "1.5 2.5", "9 3 2 3"] as const;

/** How many textures exist, solid included — the tier axis of the pair. */
export const DASH_TIER_COUNT = SERIES_DASH.length;

/**
 * Stage name → its `[hue, tier]` pair. The single source of truth for what a
 * stage looks like, everywhere.
 *
 * THE EIGHT RUNGS OWN THE EIGHT HUES AT TIER 0. Only `NU Interview` moved when
 * the collisions were unpicked on 2026-08-27 (indigo → pink); the other seven
 * keep the hue they have always had, so the correction costs the smallest
 * possible relearning.
 *
 * THE FIVE AUXILIARY STAGES EACH MOVED ONTO A NEW HUE and gained tier 1. Every
 * one of the five changed hue on 2026-08-27 — none kept the one it had. What
 * matters is which hue it moved ONTO: not the one belonging to the rung it was
 * being confused with. So each of the five reported pairs (`Identified`/`NU
 * Interview`, `Carve Out`/`Jury Member`, `Offer`/`Client Contact`,
 * `Initial Screen`/`Reference`, `Client Interview`/`Stakeholder`) is separated
 * by HUE — which is sufficient on its own, and is what
 * `searchDetail.unit.test.tsx` asserts. The tier is the second line of defence:
 * it separates an auxiliary stage from the DIFFERENT rung it now shares a hue
 * with. Two of the five pairs happen to share a tier (`Identified`/`NU
 * Interview` are both tier 0, `Carve Out`/`Jury Member` both tier 1); the hue
 * already tells them apart, so that is not a gap.
 *
 * Pinned by a test: thirteen names, thirteen distinct pairs.
 */
const STAGE_STYLE: Readonly<Record<string, readonly [hue: number, tier: number]>> = {
  // The ladder, in progression order. Tier 0, one hue each.
  Source: [4, 0], // cyan
  Identified: [5, 0], // indigo
  Pursuing: [1, 0], // amber
  "Initial Screen": [2, 0], // violet
  "NU Interview": [6, 0], // pink — WAS indigo, colliding with Identified on every live search
  "Client Interview": [3, 0], // green
  Offer: [7, 0], // blue
  Placed: [0, 0], // brand teal — the outcome that matters
  // Off the ladder. Tier 1, so each is a DASHED version of a rung's hue rather
  // than a second copy of it.
  "Carve Out": [4, 1], // dashed cyan
  "Jury Member": [1, 1], // dashed amber
  "Client Contact": [0, 1], // dashed teal
  Reference: [5, 1], // dashed indigo
  Stakeholder: [6, 1], // dashed pink
};

/** Stages that render neutral grey and solid — a rule, not an assignment. */
const NEUTRAL_STAGES: ReadonlySet<string> = new Set([
  "Rejected",
  "Unknown",
  "Unresponsive",
  "On Hold",
  "Backburner",
]);

/** A `[hue, tier]` pair flattened to one integer, for set membership. */
function slotKey(hue: number, tier: number): number {
  return tier * SERIES_FILL.length + hue;
}

/**
 * Every pair {@link STAGE_STYLE} does NOT claim, in a fixed order.
 *
 * Tier-major, so an unmapped stage reaches for an unused HUE before it reaches
 * for a texture: a tenant stage is likelier to be legible as its own colour
 * than as the third dash of a hue it shares with two named stages.
 */
const FREE_SLOTS: readonly (readonly [number, number])[] = (() => {
  const claimed = new Set(Object.values(STAGE_STYLE).map(([hue, tier]) => slotKey(hue, tier)));
  const free: [number, number][] = [];
  for (let tier = 0; tier < SERIES_DASH.length; tier += 1) {
    for (let hue = 0; hue < SERIES_FILL.length; hue += 1) {
      if (!claimed.has(slotKey(hue, tier))) free.push([hue, tier]);
    }
  }
  return free;
})();

/** FNV-1a-ish label hash. Deterministic, so an unmapped stage keeps its style
 *  across renders, across widgets and across searches. */
function hashLabel(label: string): number {
  let hash = 2166136261;
  for (let i = 0; i < label.length; i += 1) {
    hash ^= label.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

/** How one stage is drawn, in every widget that draws stages. */
export interface StageSeriesStyle {
  /** SVG `fill` utility class — donut arcs, funnel rungs, crosshair markers. */
  fillClass: string;
  /** SVG `stroke` utility class — line series and legend swatches. */
  strokeClass: string;
  /** `background-color` utility class — legend dots. */
  bgClass: string;
  /** SVG `stroke-dasharray`; `""` means solid. */
  dash: string;
  /**
   * The texture index behind {@link dash}. `0` is solid; `1`-`3` also select a
   * hatch for the filled widgets, which cannot render a dash — see
   * `StageHatch.tsx`.
   */
  tier: number;
}

const NEUTRAL_STYLE: StageSeriesStyle = {
  fillClass: "fill-chart-neutral",
  strokeClass: "stroke-chart-neutral",
  bgClass: "bg-chart-neutral",
  dash: "",
  tier: 0,
};

/**
 * How one stage is drawn — the entry point every stage widget goes through.
 *
 * A pure function of the label, which is the whole design: the donut, the
 * funnel, the two progress line charts and the downloaded report all call this
 * and therefore cannot disagree, whatever set each of them happens to draw.
 *
 * Neutral stages return grey and solid, and are the one place two stages
 * deliberately look identical.
 */
export function stageSeriesStyle(stage: string): StageSeriesStyle {
  if (NEUTRAL_STAGES.has(stage)) return NEUTRAL_STYLE;
  const [hue, tier] = STAGE_STYLE[stage] ?? FREE_SLOTS[hashLabel(stage) % FREE_SLOTS.length];
  return {
    fillClass: SERIES_FILL[hue],
    strokeClass: SERIES_STROKE[hue],
    bgClass: SERIES_BG[hue],
    dash: SERIES_DASH[tier],
    tier,
  };
}

/** SVG `fill` utility class for a stage. */
export function stageFillClass(label: string): string {
  return stageSeriesStyle(label).fillClass;
}

/** SVG `stroke` utility class for a stage — the line-chart counterpart. */
export function stageStrokeClass(label: string): string {
  return stageSeriesStyle(label).strokeClass;
}

/** `background-color` utility class for a stage (legend swatch, bar fill). */
export function stageBgClass(label: string): string {
  return stageSeriesStyle(label).bgClass;
}

/**
 * The SIZE OF THE STYLE SPACE — how many distinct (hue, tier) pairs exist.
 *
 * NOT a promise that thirty-two stages will look different from one another.
 * The thirteen MAPPED names are hand-assigned and provably distinct; everything
 * else is hashed into the free slots, and hashed keys can collide well before
 * the space is full — the birthday bound, not the capacity, is what governs
 * there. Read this as the denominator, not as a guarantee.
 *
 * Published so the day a real search outgrows the space is visible in a test
 * rather than on a chart. Thirty-two against thirteen mapped names leaves room
 * the eight-hue scale alone did not have.
 */
export const DISTINCT_STAGE_STYLES = SERIES_FILL.length * SERIES_DASH.length;

/**
 * Contact method → series index, for the Outreach Activity chart.
 *
 * Its own map rather than reusing {@link STAGE_STYLE}: methods and stages are
 * different vocabularies that happen to share a palette, and folding them
 * together would mean a stage rename could silently recolour a method. The four
 * live values are Thrive's own (`Email`, `LinkedIn`, `Text`, `Phone`); anything
 * else falls back to the same label hash the stages use, which keeps an unknown
 * method's colour stable across renders.
 *
 * NO TIER HERE, and that is arithmetic rather than an oversight: four methods
 * against eight hues needs no second channel. If Thrive ever reports a ninth
 * method this map grows a tier the way the stages did.
 *
 * `Unknown` renders neutral for the same reason `Rejected` does on a stage
 * chart — a bucket that means "we do not know" should not read as a fifth kind
 * of outreach.
 */
const METHOD_SERIES: Readonly<Record<string, number>> = {
  LinkedIn: 7, // blue
  Email: 0, // brand teal
  Phone: 3, // green
  Text: 1, // amber
};

function methodIndex(label: string): number {
  const mapped = METHOD_SERIES[label];
  if (mapped !== undefined) return mapped;
  return hashLabel(label) % SERIES_STROKE.length;
}

/** SVG `stroke` utility class for one outreach contact method. */
export function methodStrokeClass(label: string): string {
  if (label === "Unknown") return "stroke-chart-neutral";
  return SERIES_STROKE[methodIndex(label)];
}

/** `background-color` utility class for one outreach method (legend swatch). */
export function methodBgClass(label: string): string {
  if (label === "Unknown") return "bg-chart-neutral";
  return SERIES_BG[methodIndex(label)];
}

/**
 * SVG `fill` utility class for one outreach method — the crosshair marker sitting
 * on that method's line.
 *
 * Its own function rather than `methodBgClass(...).replace("bg-", "fill-")`:
 * Tailwind cannot see a class assembled at runtime, so that swap only renders at
 * all because the `fill-chart-*` literals happen to exist elsewhere in the
 * bundle — a coincidence, and one that would break silently the day the last
 * literal use of a given index went away.
 */
export function methodFillClass(label: string): string {
  if (label === "Unknown") return "fill-chart-neutral";
  return SERIES_FILL[methodIndex(label)];
}
